import { useQuery } from '@tanstack/react-query'
import type { ColumnDef } from '@tanstack/react-table'
import { useMemo } from 'react'
import { useTranslate } from 'shared/lib/i18n'
import type { RouterOutputs } from 'shared/api'
import { trpc } from 'shared/api'
import { DataTable } from 'shared/components/data-table/data-table'
import { Badge } from 'shared/components/ui/badge'
import { HubShelfBoxes } from 'shared/components/hub-shelf-boxes'
import { useDataTable } from 'shared/hooks/use-data-table'
import { SectionTitle } from 'shared/components/section'

/**
 * The hub shelf (CONTEXT — hub shelf; spec §12, user story 65; ADR-0026): every
 * Parcel the hub holds that cannot go onward, oldest first, each with why it
 * waits — cancelled but arrived, not on the Manifest, no known Point, … Support
 * decides each one's fate; the hub worker only sees where it stands.
 */
export type HubShelfItem = RouterOutputs['hubReceiving']['Shelf'][number]

export function HubShelf() {
	const { t } = useTranslate()
	const query = useQuery(trpc.hubReceiving.Shelf.queryOptions())
	const boxes = useQuery(trpc.hubReceiving.ShelfBoxes.queryOptions())

	return (
		<div className='flex flex-col gap-8'>
			<section className='flex flex-col gap-2'>
				<SectionTitle title={t('hub.shelfTitle')} count={query.data?.length} hint={t('hub.shelfExplain')} />
				{query.isLoading && <p className='text-sm text-muted-foreground'>{t('hub.loading')}</p>}
				{query.isError && (
					<p className='text-sm text-destructive' data-testid='hub-shelf-error'>
						{t('hub.loadFailed', { message: query.error.message })}
					</p>
				)}
				{query.isSuccess && <HubShelfList items={query.data} />}
			</section>
			<HubShelfBoxes items={boxes.data ?? []} title={t('hub.shelfBoxesTitle')} />
		</div>
	)
}

/** The shelf's rows, each with its reasons. A function of its items. */
export function HubShelfList({ items }: { items: readonly HubShelfItem[] }) {
	const { t } = useTranslate()
	const columns = useMemo<ColumnDef<HubShelfItem>[]>(
		() => [
			{
				id: 'code',
				header: t('hub.shelfCode'),
				cell: ({ row }) => <span className='font-mono'>{row.original.trackingCode}</span>,
			},
			{
				id: 'reasons',
				header: t('hub.shelfReasons'),
				cell: ({ row }) => (
					<div className='flex flex-wrap gap-1' data-testid='hub-shelf-reasons'>
						{row.original.reasons.map((r) => (
							<Badge
								key={r}
								variant='outline'
								className='border-amber-500/60 text-amber-700 dark:text-amber-400'
								data-reason={r}
							>
								{t(`hubShelfReason.${r}`) || r}
							</Badge>
						))}
					</div>
				),
			},
			{ id: 'partner', header: t('hub.shelfPartner'), cell: ({ row }) => row.original.partnerName ?? t('hub.noPartner') },
			{
				id: 'point',
				header: t('hub.shelfPoint'),
				cell: ({ row }) =>
					row.original.pointCode ?? <span className='text-muted-foreground'>{t('hub.noPoint')}</span>,
			},
			{
				id: 'batch',
				header: t('hub.shelfBatch'),
				cell: ({ row }) => <span className='text-muted-foreground'>{row.original.dispatchRef ?? '—'}</span>,
			},
		],
		[t],
	)
	const data = items as HubShelfItem[]
	const { table } = useDataTable({
		data,
		columns,
		rowCount: data.length,
		manualPagination: false,
		manualSorting: false,
		manualFiltering: false,
		enableRowSelection: false,
		getRowId: (p) => p.parcelId,
		initialState: { pagination: { pageIndex: 0, pageSize: 1000 } },
	})

	return (
		<div data-testid='hub-shelf'>
			<DataTable
				table={table}
				withPagination={false}
				emptyText={<span data-testid='hub-shelf-empty'>{t('hub.shelfEmpty')}</span>}
				row={{ attributes: (p) => ({ 'data-testid': `hub-shelf-${p.parcelId}` }) }}
			/>
		</div>
	)
}
